import { useState, useEffect } from 'react';
import { axios } from '../App';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle, XCircle, Activity, TrendingUp, Bell, Wifi, WifiOff } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function Monitoring() {
  const [sensorHealth, setSensorHealth] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ws, setWs] = useState(null);

  useEffect(() => {
    loadData();
    
    const token = localStorage.getItem('token');
    const wsUrl = `${process.env.REACT_APP_BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://')}/api/ws/sensor-stream`;
    const websocket = new WebSocket(wsUrl);
    
    websocket.onopen = () => {
      console.log('WebSocket connected');
      setWs(websocket);
    };
    
    websocket.onmessage = (event) => {
      const message = JSON.parse(event.data);
      if (message.type === 'alert') {
        toast.error(message.data.title);
        loadData();
      } else if (message.type === 'sensor_reading') {
        console.log('Live sensor reading:', message.data);
      }
    };
    
    websocket.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
    
    websocket.onclose = () => {
      console.log('WebSocket disconnected');
      setWs(null);
    };
    
    const interval = setInterval(loadData, 30000);
    
    return () => {
      clearInterval(interval);
      if (websocket) {
        websocket.close();
      }
    };
  }, []);

  const loadData = async () => {
    try {
      const [healthRes, alertsRes] = await Promise.all([
        axios.get('/sensor-health'),
        axios.get('/alerts')
      ]);
      setSensorHealth(healthRes.data);
      setAlerts(alertsRes.data);
    } catch (error) {
      toast.error('Failed to load monitoring data');
    } finally {
      setLoading(false)
;
    }
  };

  const handleAcknowledge = async (alertId) => {
    try {
      await axios.post(`/alerts/${alertId}/acknowledge`);
      toast.success('Alert acknowledged');
      loadData();
    } catch (error) {
      toast.error('Failed to acknowledge alert');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-sky-500 font-mono text-sm">LOADING SYSTEM MONITORING...</div>
      </div>
    );
  }

  const healthyCount = sensorHealth.filter(s => s.status === 'healthy').length;
  const degradedCount = sensorHealth.filter(s => s.status === 'degraded').length;
  const offlineCount = sensorHealth.filter(s => s.status === 'offline').length;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight uppercase">System Monitoring</h1>
          <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1 font-mono">
            Real-time health & alerts
          </p>
        </div>
        <div className="flex items-center gap-2">
          {ws ? (
            <>
              <Wifi className="text-emerald-500 animate-pulse" size={20} />
              <span className="text-xs font-mono text-emerald-500 uppercase">Live Connected</span>
            </>
          ) : (
            <>
              <WifiOff className="text-red-500" size={20} />
              <span className="text-xs font-mono text-red-500 uppercase">Disconnected</span>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-zinc-950/80 border border-emerald-800 rounded-sm p-6">
          <div className="flex items-center gap-3 mb-2">
            <CheckCircle className="text-emerald-500" size={24} />
            <span className="text-xs font-mono text-zinc-500 uppercase">Healthy</span>
          </div>
          <div className="text-3xl font-bold text-emerald-500">{healthyCount}</div>
        </div>
        <div className="bg-zinc-950/80 border border-amber-800 rounded-sm p-6">
          <div className="flex items-center gap-3 mb-2">
            <AlertTriangle className="text-amber-500" size={24} />
            <span className="text-xs font-mono text-zinc-500 uppercase">Degraded</span>
          </div>
          <div className="text-3xl font-bold text-amber-500">{degradedCount}</div>
        </div>
        <div className="bg-zinc-950/80 border border-red-800 rounded-sm p-6">
          <div className="flex items-center gap-3 mb-2">
            <XCircle className="text-red-500" size={24} />
            <span className="text-xs font-mono text-zinc-500 uppercase">Offline</span>
          </div>
          <div className="text-3xl font-bold text-red-500">{offlineCount}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight uppercase mb-4">Sensor Health</h2>
          <div className="space-y-3">
            {sensorHealth.map((sensor) => (
              <div
                key={sensor.sensor_id}
                className={`bg-zinc-950/80 border rounded-sm p-4 ${
                  sensor.status === 'healthy'
                    ? 'border-emerald-800'
                    : sensor.status === 'degraded'
                    ? 'border-amber-800'
                    : 'border-red-800'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      {sensor.status === 'healthy' ? (
                        <CheckCircle className="text-emerald-500" size={18} />
                      ) : sensor.status === 'degraded' ? (
                        <AlertTriangle className="text-amber-500" size={18} />
                      ) : (
                        <XCircle className="text-red-500" size={18} />
                      )}
                      <span className="font-bold text-sm">{sensor.sensor_name}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs text-zinc-500">
                      <div>Status: {sensor.status}</div>
                      <div>Health: {sensor.health_score.toFixed(0)}%</div>
                      <div>Readings (5m): {sensor.reading_count_5min}</div>
                      {sensor.last_reading && (
                        <div>Last: {new Date(sensor.last_reading).toLocaleTimeString()}</div>
                      )}
                    </div>
                  </div>
                  <div className="w-16 h-16 flex items-center justify-center">
                    <Activity
                      className={
                        sensor.status === 'healthy'
                          ? 'text-emerald-500'
                          : sensor.status === 'degraded'
                          ? 'text-amber-500'
                          : 'text-red-500'
                      }
                      size={32}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h2 className="text-2xl font-semibold tracking-tight uppercase mb-4">Active Alerts</h2>
          <div className="space-y-3">
            {alerts.filter(a => !a.acknowledged).length === 0 ? (
              <div className="text-center py-8 text-zinc-600 font-mono text-sm">
                No active alerts
              </div>
            ) : (
              alerts
                .filter(a => !a.acknowledged)
                .map((alert) => (
                  <div
                    key={alert.id}
                    className={`bg-zinc-950/80 border rounded-sm p-4 ${
                      alert.severity === 'critical'
                        ? 'border-red-800'
                        : alert.severity === 'high'
                        ? 'border-amber-800'
                        : 'border-yellow-800'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <Bell
                        className={
                          alert.severity === 'critical'
                            ? 'text-red-500'
                            : alert.severity === 'high'
                            ? 'text-amber-500'
                            : 'text-yellow-500'
                        }
                        size={20}
                      />
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-sm font-bold">{alert.title}</h3>
                          <span
                            className={`text-xs font-mono uppercase px-2 py-1 rounded-sm ${
                              alert.severity === 'critical'
                                ? 'bg-red-900/50 text-red-300 border border-red-800'
                                : alert.severity === 'high'
                                ? 'bg-amber-900/50 text-amber-300 border border-amber-800'
                                : 'bg-yellow-900/50 text-yellow-300 border border-yellow-800'
                            }`}
                          >
                            {alert.severity}
                          </span>
                        </div>
                        <p className="text-xs text-zinc-400 mb-3">{alert.description}</p>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-zinc-600">
                            {new Date(alert.created_at).toLocaleString()}
                          </span>
                          <Button
                            onClick={() => handleAcknowledge(alert.id)}
                            className="bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-none uppercase text-xs px-3 py-1 h-auto"
                          >
                            Acknowledge
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
